import { computeWorkflowProgress } from 'features/libra/helpers/getWorkflowPercComplete'
import { getLifecycleStage } from 'features/libra/lifecycle/lifecycleStageHelpers'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'

export enum AssetStatus {
  NotStarted = 'not-started',
  InProgress = 'in-progress',
  PendingReview = 'pending-review',
  Completed = 'completed',
}

/**
 * Stage id conventionally used by review-gated `lifecycleConfig`s (see
 * `features/libra/README.md`) for "workflow finished, awaiting reviewer
 * action". Not every workflow has this stage -- `getAssetStatus` treats
 * tickets without it exactly as before.
 * TODO: this should be defined in the right place later
 */
const PENDING_REVIEW_STAGE_ID = 'pending_review'

/**
 * Calculates the status of an asset based on its tickets. Delegates per-ticket
 * progress + status to `computeWorkflowProgress`, and consults each ticket's
 * lifecycle stage (when its workflow has a `lifecycleConfig`) to distinguish
 * "submitted, awaiting review" from "actually done".
 */
export function getAssetStatus(
  unitTickets: ReadonlyArray<LibraTicket>,
  workflowMap: Map<string, LibraWorkflow>
): AssetStatus {
  const hasInProgress = unitTickets.some((ticket) => {
    if (ticket.workflowComplete) return false
    const workflow = workflowMap.get(ticket.workflowId)
    if (!workflow) return false
    return computeWorkflowProgress(ticket, workflow).status === 'in-progress'
  })
  if (hasInProgress) {
    return AssetStatus.InProgress
  }

  const hasPendingReview = unitTickets.some((ticket) => {
    const lifecycleConfig = workflowMap.get(ticket.workflowId)?.lifecycleConfig
    return (
      getLifecycleStage(ticket, lifecycleConfig)?.id === PENDING_REVIEW_STAGE_ID
    )
  })

  if (hasPendingReview) {
    return AssetStatus.PendingReview
  }

  if (unitTickets.some((ticket) => ticket.workflowComplete)) {
    return AssetStatus.Completed
  }

  return AssetStatus.NotStarted
}
